import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import AuthService from "./Services/AuthService.js";
import { handleAuthFailure } from "../../Utility/authSession.js";

export default function AuthWrapper({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const isUserActive = useRef(false);

  useEffect(() => {
    let isMounted = true;
    const verify = async () => {
      const token = Cookies.get("JWTToken");
      if (!token) {
        navigate("/", { replace: true });
        return;
      }

      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const expiryDate = new Date(payload.exp * 1000);
          if (expiryDate <= new Date()) {
            handleAuthFailure("Your session has expired. Please log in again.");
            return;
          }
        } catch (e) {
        }
      }

      try {
        const result = await AuthService.VerifyUserAPI();
        if (!isMounted) return;

        if (result?.status?.toLowerCase() === "success") {
          setIsAuthorized(true);
        } else {
          handleAuthFailure(
            result?.message || "Session is no longer valid. Please log in again."
          );
        }
      } catch (error) {
        if (!isMounted) return;
        handleAuthFailure(
          error?.response?.data?.message ||
          "Session verification failed. Please log in again."
        );
      } finally {
        if (isMounted) setIsVerifying(false);
      }
    };

    verify();
    return () => {
      isMounted = false;
    };
  }, [navigate, location.pathname]); // Re-verify on route changes

  useEffect(() => {
    if (!isAuthorized) return;
    const markActive = () => {
      isUserActive.current = true;
    };
    window.addEventListener("mousemove", markActive);
    window.addEventListener("keydown", markActive);
    window.addEventListener("click", markActive);
    window.addEventListener("scroll", markActive);

    const checkExpiryInterval = setInterval(async () => {
      const token = Cookies.get("JWTToken");
      if (!token) return;

      let expiryDate;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        expiryDate = new Date(payload.exp * 1000);
      } catch (e) {
        return;
      }
      const now = new Date();

      const timeDiffMinutes = (expiryDate - now) / 1000 / 60;
      if (timeDiffMinutes > 0 && timeDiffMinutes <= 5 && isUserActive.current) {
        try {
          const result = await AuthService.VerifyUserAPI();

          if (result?.status?.toLowerCase() === "success" && result.isRefreshed) {
            Cookies.set("JWTToken", result.jwtToken, { expires: 1, secure: false, sameSite: "Lax" });
            if (result.tokenExpiry) {
              Cookies.set("tokenExpiry", result.tokenExpiry, { expires: 1, secure: false, sameSite: "Lax" });
            }
            isUserActive.current = false;
          }
        } catch (error) {
        }
      } else if (timeDiffMinutes <= 0) {
        handleAuthFailure("Your session has expired due to inactivity. Please log in again.");
      }
    }, 60000);

    return () => {
      window.removeEventListener("mousemove", markActive);
      window.removeEventListener("keydown", markActive);
      window.removeEventListener("click", markActive);
      window.removeEventListener("scroll", markActive);
      clearInterval(checkExpiryInterval);
    };
  }, [isAuthorized]);

  if (isVerifying || !isAuthorized) return null;
  return children;
}